import { useCallback, useRef, useState, type FocusEvent, type FormEvent } from "react"
import { useTranslation } from "react-i18next"

type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

const isFieldElement = (el: EventTarget | null): el is FieldElement => {
    return !!el && 'validity' in el && typeof (el as FieldElement).checkValidity === 'function'
}

export function useFieldValidity() {
    const { t } = useTranslation()
    const interactedRef = useRef(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const update = useCallback((el: EventTarget | null, markInteracted = false) => {
        if (!isFieldElement(el)) {
            return
        }
        if (markInteracted) {
            interactedRef.current = true
        }
        if (!interactedRef.current) {
            return
        }
        if (el.checkValidity()) {
            setErrorMessage(null)
            return
        }
        setErrorMessage(el.validity.valueMissing ? t('required') : t('invalid'))
    }, [t])

    const onBlur = useCallback((e: FocusEvent<FieldElement>) => {
        update(e.target, true)
    }, [update])

    const onChange = useCallback((e: FormEvent<FieldElement>) => {
        update(e.target, true)
    }, [update])

    const onInvalid = useCallback((e: FormEvent<FieldElement>) => {
        e.preventDefault()
        update(e.target, true)
    }, [update])

    return {
        errorMessage,
        isInvalid: Boolean(errorMessage),
        fieldClassName: `peer ${errorMessage ? '!border-[#FF4747]' : ''}`,
        onBlur,
        onChange,
        onInvalid,
    }
}

const FieldError = ({ message }: { message: string | null }) => {
    const { t } = useTranslation()
    return (
        <p className={`text-xs text-[#FF4747] mt-0.5 ${message ? 'block' : 'hidden peer-user-invalid:block'}`}>
            {message || t('required')}
        </p>
    )
}

export default FieldError
